import React, { useEffect, useRef, useState } from 'react';

let plotlyPromise;
const loadPlotly = () => {
  if (!plotlyPromise) {
    plotlyPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = '/tech/plotly.min.js';
      s.onload = () => resolve(window.Plotly);
      s.onerror = reject;
      document.body.appendChild(s);
    });
  }
  return plotlyPromise;
};

let dataPromise;
const loadData = () => (dataPromise = dataPromise || fetch('/tech/media/benchmark/benchmark.json').then((r) => r.json()));

const ink = 'rgba(255,255,255,0.75)', grid = 'rgba(255,255,255,0.08)', blue = '#4F8CFF', orange = '#FF7A00', green = '#22C55E';
const base = (extra) => ({
  font: { family: 'Inter, sans-serif', size: 12, color: ink }, paper_bgcolor: '#111111', plot_bgcolor: '#111111',
  margin: { l: 60, r: 20, t: 16, b: 48 }, hovermode: 'closest', showlegend: true, legend: { orientation: 'h', y: -0.22 }, ...extra,
});
const ax = (title, extra) => ({ title: { text: title }, gridcolor: grid, zerolinecolor: grid, linecolor: 'rgba(255,255,255,0.3)', ...extra });
const cfg = { responsive: true, displaylogo: false, modeBarButtonsToRemove: ['lasso2d', 'select2d'] };

const usePlot = (draw, deps) => {
  const ref = useRef(null);
  useEffect(() => {
    let alive = true;
    Promise.all([loadPlotly(), loadData()]).then(([Plotly, B]) => alive && ref.current && draw(Plotly, B, ref.current));
    return () => { alive = false; };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return ref;
};

export const AlignChart = () => {
  const ref = usePlot((Plotly, B, el) => {
    const D = B.detail.flow;
    Plotly.newPlot(el, [
      { type: 'scatter', mode: 'lines', name: 'Gyroscope (°/s)', x: D.t, y: D.gyro_dps, line: { color: blue, width: 1.6 } },
      { type: 'scatter', mode: 'lines', name: 'Image motion (px/frame)', x: D.t, y: D.flow_px, yaxis: 'y2', line: { color: orange, width: 1.4 } },
    ], base({
      hovermode: 'x unified', xaxis: ax('Seconds'), yaxis: ax('Gyroscope (°/s)'),
      yaxis2: ax('Image motion (px/frame)', { overlaying: 'y', side: 'right', showgrid: false }), margin: { l: 60, r: 64, t: 16, b: 48 },
    }), cfg);
  }, []);
  return <div ref={ref} className="w-full h-[380px] rounded-2xl border border-white/10 overflow-hidden bg-[#111111]" data-testid="chart-align" />;
};

const scene3d = { backgroundcolor: '#111111', gridcolor: grid, zerolinecolor: grid, color: ink };

export const TrajectoryChart = () => {
  const [labels, setLabels] = useState([]);
  const [pick, setPick] = useState(0);
  useEffect(() => { loadData().then((B) => setLabels(B.trajectories.map((t) => t.label))); }, []);
  const ref = usePlot((Plotly, B, el) => {
    const t = B.trajectories[pick];
    Plotly.react(el, [
      { type: 'scatter3d', mode: 'lines', x: t.x, y: t.y, z: t.z, name: 'Camera path', showlegend: false, line: { width: 5, color: t.x.map((_, i) => i), colorscale: 'Viridis' }, hovertemplate: 'x %{x:.3f}<br>y %{y:.3f}<br>z %{z:.3f}<extra></extra>' },
      { type: 'scatter3d', mode: 'markers', x: [0], y: [0], z: [0], name: 'Start', marker: { size: 5, color: green } },
    ], base({ margin: { l: 0, r: 0, t: 8, b: 8 }, scene: { xaxis: { title: 'x', ...scene3d }, yaxis: { title: 'y', ...scene3d }, zaxis: { title: 'z', ...scene3d }, aspectmode: 'data' } }), cfg);
  }, [pick]);
  return (
    <div>
      <label className="flex flex-wrap items-center gap-3 mb-3 text-sm text-white/50">
        Recording
        <select value={pick} onChange={(e) => setPick(+e.target.value)} data-testid="trajectory-select"
          className="w-full sm:w-auto max-w-full min-w-0 bg-[#141414] border border-white/10 rounded-full px-4 py-1.5 text-sm text-white focus:outline-none focus:border-[#FF7A00]/60">
          {labels.map((l, i) => <option key={l} value={i}>{l} (JanData Cap)</option>)}
        </select>
      </label>
      <div ref={ref} className="w-full h-[520px] rounded-2xl border border-white/10 overflow-hidden bg-[#111111]" data-testid="chart-trajectory" />
    </div>
  );
};
